import { describeSolutionSourceState } from '../../utils/solution-source.js';
import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { Exam } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { Submission } from '../../submission.js';
import { awardedPoints } from '../../utils/points-model.js';
import { getMasterSolutionsQuery } from '../../api/get-master-solutions.js';
import { GRADING_THRESHOLDS } from '../../../shared/constants.js';

/**
 * Context the chat agent loop hands to every query tool.
 * `currentExamId` is the exam linked to the current conversation (may be null if no exam context exists yet).
 */
export interface QueryToolContext {
  currentExamId: string | null;
}

/** The backend always answers query tools with pre-assembled German freetext (never raw JSON). */
type QueryToolResult = { content: string };

const ID_HINT =
  '\n\nHINWEIS: IDs sind nur für interne Tool-Aufrufe gedacht. Nenne dem Nutzer NIEMALS IDs – sprich mit ihm ausschließlich über die Namen der Klausuren und Schüler.';

const masterSolutionSelection = {
  taskNumber: 1,
  displayTaskNumber: 1,
  description: 1,
  masterSolutionContent: 1,
  gradingCriteria: 1,
  gradingMaxPoints: 1,
  needsAnswer: 1,
} as const;

// ---------------------------------------------------------------------------
// Detail tiers (top-of-file control: one place decides how much each tool emits)
//
// As the number of exams/submissions grows, the freetext payload would explode
// (and blow the model's context). Instead of always returning everything, the
// two query tools pick a detail tier up front and the renderers honour it.
// Helpers stay isolated and side-effect-free so the thresholds are easy to tune.
// ---------------------------------------------------------------------------

/** How much detail `query_submissions` returns, picked once from the matched volume. */
type SubmissionTier = 'FULL' | 'TASK_SCORES' | 'TOTALS_ONLY' | 'EXAM_AVERAGE';

/** How much detail `query_exams` returns, picked once from the number of exams. */
type ExamTier = 'FULL_DETAILS' | 'COMPARE_MODE' | 'STATE_ONLY' | 'METADATA_ONLY';

/**
 * Decides the submission detail tier (waterfall):
 * - `isSingleTaskQuery` (exactly 1 exam AND exactly 1 task path): FULL up to 100 abgaben, else TASK_SCORES.
 * - single exam (multi-task): FULL ≤3, TASK_SCORES 4–35, else TOTALS_ONLY.
 * - cross-exam (>1): FULL ≤3, TASK_SCORES 4–35, TOTALS_ONLY 36–100, else EXAM_AVERAGE.
 */
function getSubmissionTier(
  requestsCount: number,
  totalSubmissions: number,
  isSingleTaskQuery: boolean,
): SubmissionTier {
  if (isSingleTaskQuery) {
    return totalSubmissions <= 100 ? 'FULL' : 'TASK_SCORES';
  }
  if (requestsCount <= 1) {
    if (totalSubmissions <= 3) return 'FULL';
    if (totalSubmissions <= 35) return 'TASK_SCORES';
    return 'TOTALS_ONLY';
  }
  // Cross-exam (requestsCount > 1).
  if (totalSubmissions <= 3) return 'FULL';
  if (totalSubmissions <= 35) return 'TASK_SCORES';
  if (totalSubmissions <= 100) return 'TOTALS_ONLY';
  return 'EXAM_AVERAGE';
}

/**
 * Decides the exam detail tier from the number of queried exams:
 * 1 → FULL_DETAILS, 2–3 → COMPARE_MODE (drop text-heavy master solution / grading scheme),
 * 4–5 → STATE_ONLY (no task content, status still allowed),
 * >5 → METADATA_ONLY (only base metadata — no task content AND no status, which can be huge per exam).
 */
function getExamTier(requestsCount: number): ExamTier {
  if (requestsCount <= 1) return 'FULL_DETAILS';
  if (requestsCount <= 3) return 'COMPARE_MODE';
  if (requestsCount <= 5) return 'STATE_ONLY';
  return 'METADATA_ONLY';
}

/**
 * Hard cap on how many exams a single query_exams / query_submissions call may touch. Protects both the
 * model's context AND the backend (each exam is a DB lookup + a submissions fetch). For a pure overview of
 * more exams, the agent should use get_available_options (one cheap query) instead.
 */
const MAX_EXAMS_PER_QUERY = 20;

/** Diagnostic hint emitted when more exams than the hard cap were requested and the rest were dropped. */
function examCapHint(droppedCount: number): string {
  return (
    `[SYSTEM] HINWEIS: Es wurden nur die ersten ${MAX_EXAMS_PER_QUERY} Klausuren verarbeitet, ${droppedCount} weitere wurden ignoriert. ` +
    `Starte KEINE Folgeabfragen, um die übrigen nachzuladen. Für eine reine Übersicht aller Klausuren nutze get_available_options.`
  );
}

/** Detail ordering (0 = finest/most detail … 3 = coarsest/most aggregated). Used to clamp agent overrides. */
const SUBMISSION_TIER_RANK: Record<SubmissionTier, number> = {
  FULL: 0,
  TASK_SCORES: 1,
  TOTALS_ONLY: 2,
  EXAM_AVERAGE: 3,
};

/** Maps the optional `detailLevel` override arg to a tier (null = no/unknown override). */
function parseRequestedSubmissionTier(value: unknown): SubmissionTier | null {
  if (typeof value !== 'string') return null;
  switch (value.trim().toLowerCase()) {
    case 'full':
      return 'FULL';
    case 'task_scores':
    case 'task-scores':
    case 'scores':
      return 'TASK_SCORES';
    case 'totals':
    case 'totals_only':
      return 'TOTALS_ONLY';
    case 'exam_average':
    case 'average':
      return 'EXAM_AVERAGE';
    default:
      return null;
  }
}

/**
 * The agent may voluntarily ask for a COARSER (more aggregated) tier than the automatic one — e.g. only
 * totals for a small exam to save context. It may NOT force a finer tier than the volume-based cap allows;
 * such a request is clamped back to `autoTier` (the caller surfaces a diagnostic hint about the clamp).
 */
function coarserTier(autoTier: SubmissionTier, requested: SubmissionTier | null): SubmissionTier {
  if (!requested) return autoTier;
  return SUBMISSION_TIER_RANK[requested] >= SUBMISSION_TIER_RANK[autoTier] ? requested : autoTier;
}

/**
 * Short closing nudge appended to reduced-detail hints: after answering with what IS available, OFFER an
 * optional drill-down. Deliberately a one-line offer (not a blocking question) so the agent doesn't over-ask.
 */
const DRILL_DOWN_SUBMISSIONS =
  ' Biete dem Nutzer anschließend optional einen gezielten Drilldown an. Vollständige Antworten/Begründungen gibt es NUR für eine einzelne Aufgabe (ganze Klasse) oder für bis zu 3 einzelne Schüler – NICHT für eine ganze Klausur. Schlage daher NIEMALS „eine bestimmte Klausur".';
const DRILL_DOWN_EXAMS =
  ' Biete dem Nutzer anschließend optional an, gezielt eine einzelne Klausur (volle Inhalte) bzw. wenige Klausuren (Status) anzusehen.';

/**
 * Diagnostic [SYSTEM] hints: explain WHY fields were dropped (volume limits) and append a short drill-down
 * offer. They never block — the agent answers with what it has, then offers the narrower next step.
 */
function getDiagnosticSystemHint(
  toolName: 'query_exams' | 'query_submissions',
  activeTier: SubmissionTier | ExamTier,
  requestedFields: {
    studentAnswer?: boolean;
    gradingResult?: boolean;
    description?: boolean;
    masterSolution?: boolean;
    gradingScheme?: boolean;
    anyTaskField?: boolean;
    anyState?: boolean;
  },
): string {
  const hints: string[] = [];

  if (toolName === 'query_submissions') {
    if (requestedFields.studentAnswer && activeTier !== 'FULL') {
      hints.push(
        '[SYSTEM] HINWEIS: Die Schülerantworten (Freitexte) wurden bei dieser Datenmenge nicht mitgeliefert.' +
          DRILL_DOWN_SUBMISSIONS,
      );
    }
    if (requestedFields.gradingResult && activeTier === 'TASK_SCORES') {
      hints.push(
        '[SYSTEM] HINWEIS: Bei dieser Datenmenge wurden nur die Punkte je Aufgabe ausgegeben; die KI-Begründungen wurden weggelassen.' +
          DRILL_DOWN_SUBMISSIONS,
      );
    }
    if (activeTier === 'TOTALS_ONLY' && (requestedFields.studentAnswer || requestedFields.gradingResult)) {
      hints.push(
        '[SYSTEM] HINWEIS: Wegen der hohen Anzahl an Abgaben wurde pro Schüler nur die Gesamtpunktzahl ausgegeben (keine einzelnen Aufgaben, Antworten oder Begründungen).' +
          DRILL_DOWN_SUBMISSIONS,
      );
    }
    if (activeTier === 'EXAM_AVERAGE') {
      hints.push(
        '[SYSTEM] HINWEIS: Wegen der sehr hohen Anzahl an Abgaben über mehrere Klausuren wurde pro Klausur nur ein Gesamtdurchschnitt ausgegeben.' +
          DRILL_DOWN_SUBMISSIONS,
      );
    }
  } else {
    if (activeTier === 'COMPARE_MODE' && (requestedFields.masterSolution || requestedFields.gradingScheme)) {
      hints.push(
        '[SYSTEM] HINWEIS: Musterlösung/Bewertungsschema wurden bei mehreren Klausuren nicht mitgeliefert.' +
          DRILL_DOWN_EXAMS,
      );
    }
    if (activeTier === 'STATE_ONLY' && requestedFields.anyTaskField) {
      hints.push(
        '[SYSTEM] HINWEIS: Aufgaben-Inhalte wurden bei mehreren Klausuren nicht mitgeliefert; nur Stammdaten und – falls angefordert – der Status.' +
          DRILL_DOWN_EXAMS,
      );
    }
    if (activeTier === 'METADATA_ONLY' && (requestedFields.anyTaskField || requestedFields.anyState)) {
      hints.push(
        '[SYSTEM] HINWEIS: Bei vielen Klausuren wurden nur die Stammdaten ausgegeben – weder Aufgaben-Inhalte noch Status.' +
          DRILL_DOWN_EXAMS,
      );
    }
  }

  return hints.join('\n');
}

// ---------------------------------------------------------------------------
// Argument normalization helpers
// ---------------------------------------------------------------------------

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(entry => String(entry).trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

/** Normalizes a single task path that may arrive as "2.a.i", ["2","a","i"] or "2". */
function normalizeTaskPath(value: unknown): string {
  if (Array.isArray(value))
    return value
      .map(part => String(part).trim())
      .filter(Boolean)
      .join('.');
  return String(value ?? '').trim();
}

/** Normalizes a list of task paths. Each element may be a dot string or an array of segments. */
function toTaskPathArray(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    const single = normalizeTaskPath(value);
    return single ? [single] : [];
  }
  return value.map(normalizeTaskPath).filter(Boolean);
}

function isCurrentRef(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === 'this' || v === 'current';
}

/** Resolves an examId argument that may be empty/"null"/"this" into a concrete id (or null = current missing). */
function resolveExamId(raw: unknown, ctx: QueryToolContext): string | null {
  const v = typeof raw === 'string' ? raw.trim() : '';
  if (!v || v.toLowerCase() === 'null' || isCurrentRef(v)) return ctx.currentExamId ?? null;
  return v;
}

/**
 * Resolves one entry of an examId list (for get_available_options).
 * Returns `''` to skip a blank/"null" entry, `null` when the entry was 'this'/'current' but no current
 * exam exists, or the concrete id otherwise.
 */
function resolveListedExamId(raw: string, ctx: QueryToolContext): string | null {
  const v = raw.trim();
  if (!v || v.toLowerCase() === 'null') return '';
  if (isCurrentRef(v)) return ctx.currentExamId ?? null;
  return v;
}

function dedupe(values: string[]): string[] {
  return [...new Set(values)];
}

// ---------------------------------------------------------------------------
// Data access (always scoped to the current user)
// ---------------------------------------------------------------------------

async function getCurrentUserId(): Promise<string | null> {
  const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
  return currentUser?._id ?? null;
}

function ownerFilter(userId: string | null): Record<string, unknown> {
  return { userID: userId ?? null };
}

interface ExamListEntry {
  id: string;
  title: string;
  createdAt: string;
}

async function listOwnedExams(userId: string | null): Promise<ExamListEntry[]> {
  const examCollection = await getCollection(Exam as any);
  const exams = await examCollection
    .find(ownerFilter(userId) as any, { projection: { title: 1, createdAt: 1 } } as any)
    .sort({ createdAt: -1 } as any)
    .toArray();
  return (exams as any[]).map(exam => ({ id: String(exam._id), title: examTitle(exam), createdAt: exam.createdAt }));
}

/** Loads a full exam document, but only if it belongs to the current user. */
async function getOwnedExam(examId: string, userId: string | null): Promise<any | null> {
  const examCollection = await getCollection(Exam as any);
  return examCollection.findOne({ _id: examId, ...ownerFilter(userId) } as any);
}

async function getSubmissionsForExam(examId: string): Promise<any[]> {
  const submissionCollection = await getCollection(Submission as any);
  return submissionCollection.find({ examId } as any).toArray() as Promise<any[]>;
}

// ---------------------------------------------------------------------------
// Small formatting helpers
// ---------------------------------------------------------------------------

function examTitle(exam: any): string {
  const title = String(exam?.title ?? '').trim();
  return title || 'Unbenannte Klausur';
}

function studentLabel(submission: any): string {
  const name = String(submission?.studentName ?? '').trim();
  return name || 'Unbenannte Abgabe';
}

function uniqueStudentNames(submissions: any[]): string[] {
  const names = new Set<string>();
  for (const submission of submissions) {
    const name = String(submission?.studentName ?? '').trim();
    if (name) names.add(name);
  }
  return [...names];
}

function formatExamList(exams: ExamListEntry[]): string {
  if (!exams.length) return '- (keine Klausuren vorhanden)';
  return exams
    .map(exam => {
      const date = new Date(exam.createdAt);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      const locales: Intl.LocalesArgument = 'de-DE';
      const formattedDate: string = date.toLocaleDateString(locales, options);
      return `- "${exam.title}" vom ${formattedDate} (ID: ${exam.id})`;
    })
    .join('\n');
}

function formatNameList(names: string[]): string {
  if (!names.length) return '- (keine vorhanden)';
  return names.map(name => `- ${name}`).join('\n');
}

function noCurrentExamBlock(exams: ExamListEntry[]): string {
  return `[SYSTEM] Es ist aktuell keine Klausur im Gesprächskontext ausgewählt. Frage den Nutzer, um welche Klausur es geht, oder verwende eine konkrete Klausur.\nVerfügbare Klausuren:\n${formatExamList(exams)}`;
}

function examNotFoundBlock(missingIds: string[], exams: ExamListEntry[]): string {
  return `[SYSTEM] Folgende Klausur-ID(s) wurden nicht gefunden oder gehören nicht zum aktuellen Nutzer: ${missingIds.join(
    ', ',
  )}.\nVerfügbare Klausuren:\n${formatExamList(exams)}`;
}

// ---------------------------------------------------------------------------
// Task / submission tree flattening
// ---------------------------------------------------------------------------

/** Collects every task path of an exam as dot-joined strings, e.g. "2", "2.a", "2.a.i". */
function collectExamTaskPaths(exam: any): string[] {
  const paths: string[] = [];
  const traverse = (node: any, ancestors: string[]) => {
    const number = String(node?.taskNumber ?? '').trim();
    const path = [...ancestors, number].filter(Boolean);
    if (path.length) paths.push(path.join('.'));
    for (const child of node?.subTasks ?? []) traverse(child, path);
  };
  for (const task of exam?.tasks ?? []) traverse(task, []);
  return paths;
}

interface FlatSubmissionTask {
  pathStr: string;
  node: any;
}

function flattenSubmissionTasks(submission: any): FlatSubmissionTask[] {
  const flat: FlatSubmissionTask[] = [];
  const traverse = (node: any, ancestors: string[]) => {
    const number = String(node?.taskNumber ?? '').trim();
    const path = [...ancestors, number].filter(Boolean);
    flat.push({ pathStr: path.join('.'), node });
    for (const child of node?.subTasksSubmission ?? []) traverse(child, path);
  };
  for (const task of submission?.taskSubmissions ?? []) traverse(task, []);
  return flat;
}

function matchesAnyPath(taskPath: string, requested: string[]): boolean {
  if (!requested.length) return true;
  return requested.some(path => taskPath === path || taskPath.startsWith(`${path}.`));
}

// ---------------------------------------------------------------------------
// examState description (selectable categories)
// ---------------------------------------------------------------------------

const KNOWN_STATE_CATEGORIES = ['workflow', 'grading', 'uncertainties'];

function stepStatusText(status: unknown): string {
  switch (status) {
    case StepState.COMPLETED:
      return 'Abgeschlossen';
    case StepState.IN_PROGRESS:
      return 'In Bearbeitung';
    case StepState.ERROR:
      return 'Fehler';
    default:
      return 'Ausstehend';
  }
}

function genStatusText(status: unknown): string {
  switch (status) {
    case StepState.COMPLETED:
      return 'Generiert';
    case StepState.IN_PROGRESS:
      return 'In Bearbeitung';
    case StepState.ERROR:
      return 'Fehler';
    default:
      return 'Noch nicht vorhanden';
  }
}

function describeWorkflow(state: any): string {
  const uploadDone = (state?.uploadProgress?.uploadedFiles ?? 0) > 0;
  return [
    '[workflow] Gesamtfortschritt:',
    `- Metadaten gesetzt: ${state?.metaDataSet ? 'Ja' : 'Nein'}`,
    `- Datei-Upload: ${uploadDone ? 'Abgeschlossen' : 'Ausstehend'}`,
    `- Aufgabenerkennung: ${stepStatusText(state?.taskExtractionProgress?.status)}`,
    `- Verarbeitung der Schülerabgaben: ${stepStatusText(state?.answerExtractionProgress?.status)}`,
    // The roll-up on the exam state, so a solution that was uploaded and then completed by the generation
    // reports as mixed instead of claiming to be purely uploaded.
    `- Musterlösung: ${
      describeSolutionSourceState(state?.masterSolutionSourceState) ??
      genStatusText(state?.masterSolutionProgress?.status)
    }`,
    `- Bewertungsschema: ${
      describeSolutionSourceState(state?.gradingSchemeSourceState) ??
      genStatusText(state?.gradingSchemeProgress?.status)
    }`,
    `- Bewertung: ${stepStatusText(state?.gradingProgress?.status)}`,
  ].join('\n');
}

function describeGrading(state: any): string {
  // Read straight from examState.gradingProgress (maintained by the grading agent);
  // total/finished are counted in task-gradings = Abgaben × bewertete Aufgaben.
  const progress = state?.gradingProgress ?? {};
  const total = Number(progress.total) || 0;
  const finished = Array.isArray(progress.finishedUnits) ? progress.finishedUnits.length : 0;
  const percent = total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0;
  const lines = [
    '[grading] Bewertung:',
    `- Status: ${stepStatusText(progress.status)}`,
    `- Bewertungsfortschritt: ${percent}% (${finished}/${total} Schülerantworten bewertet)`,
    `- Bewertung jemals gestartet: ${state?.hasGradingStarted ? 'Ja' : 'Nein'}`,
  ];
  if (progress.message) lines.push(`- Hinweis: ${String(progress.message).trim()}`);
  return lines.join('\n');
}

/** Counts region taskPaths (e.g. answer-extraction lowConfRegions) into a path -> count map. */
function countRegionPaths(regions: any[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const region of regions) {
    const path = Array.isArray(region?.taskPath) ? region.taskPath.filter(Boolean).join('.') : '';
    if (path) counts.set(path, (counts.get(path) ?? 0) + 1);
  }
  return counts;
}

/** Orders a path -> count map by the exam's task-tree order; 0-count tasks dropped, unknown paths appended. */
function orderCountsByExam(counts: Map<string, number>, exam: any): Array<[string, number]> {
  const ordered: Array<[string, number]> = [];
  const examPaths = collectExamTaskPaths(exam);
  for (const path of examPaths) {
    const count = counts.get(path);
    if (count) ordered.push([path, count]);
  }
  for (const [path, count] of counts) if (!examPaths.includes(path)) ordered.push([path, count]);
  return ordered;
}

/**
 * Mirrors the client's getConfidenceLevelSignal (grading/utils.ts): a graded task counts as uncertain when it
 * isn't approved and the lowest of [grading confidence, unapproved bounding-box confidences] is below the
 * medium threshold. This is what the "Korrektur" progress bar surfaces as grading uncertainties.
 */
function isUncertainGrading(node: any): boolean {
  const grading = node?.gradingResult?.doc ?? node?.gradingResult;
  if (!grading || grading.isApproved === true) return false;

  const candidates: number[] = [];
  const gradingConfidence = Number(grading.confidence);
  if (Number.isFinite(gradingConfidence)) candidates.push(gradingConfidence);
  for (const box of Array.isArray(node?.boundingBoxes) ? node.boundingBoxes : []) {
    if (box?.approved === true) continue;
    const boxConfidence = Number(box?.confidence);
    if (Number.isFinite(boxConfidence)) candidates.push(boxConfidence);
  }
  if (!candidates.length) return false;
  return Math.min(...candidates) < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;
}

function renderCountLines(ordered: Array<[string, number]>): string[] {
  return ordered.length ? ordered.map(([path, count]) => `- ${path}: ${count}`) : ['- keine'];
}

async function describeUncertainties(exam: any, state: any): Promise<string> {
  // Student-answer uncertainties: low-confidence regions from answer extraction, counted per task.
  const lowConfRegions = Array.isArray(state?.answerExtractionProgress?.lowConfRegions)
    ? state.answerExtractionProgress.lowConfRegions
    : [];
  const answerOrdered = orderCountsByExam(countRegionPaths(lowConfRegions), exam);

  // Grading uncertainties: low-confidence / unapproved gradings, counted per task across submissions.
  const submissions = await getSubmissionsForExam(String(exam._id));
  const gradingCounts = new Map<string, number>();
  for (const submission of submissions) {
    for (const task of flattenSubmissionTasks(submission)) {
      if (task.pathStr && isUncertainGrading(task.node)) {
        gradingCounts.set(task.pathStr, (gradingCounts.get(task.pathStr) ?? 0) + 1);
      }
    }
  }
  const gradingOrdered = orderCountsByExam(gradingCounts, exam);

  const lines = [
    '[uncertainties]',
    'Unsicherheiten in den Schülerantworten (Anzahl pro Aufgabe):',
    ...renderCountLines(answerOrdered),
    '',
    'Unsicherheiten in der Bewertung (Anzahl pro Aufgabe):',
    ...renderCountLines(gradingOrdered),
  ];

  // Guidance only when there ARE answer uncertainties: they feed into grading, so resolving them first can
  // also clear the grading uncertainties.
  if (answerOrdered.length) {
    lines.push(
      '',
      state?.hasGradingStarted === true
        ? 'Hinweis an den Nutzer: Konzentriere dich zuerst auf die Unsicherheiten in den Schülerantworten. Sie fließen in die Bewertung ein; da die Bewertung bereits gestartet wurde, wird nach deren Auflösung automatisch neu bewertet, wodurch sich auch Unsicherheiten in der Bewertung auflösen können.'
        : 'Hinweis an den Nutzer: Konzentriere dich zuerst auf die Unsicherheiten in den Schülerantworten. Sie fließen in die Bewertung ein, daher kann deren Auflösung auch die Unsicherheiten in der Bewertung vorbeugen.',
    );
  }

  return lines.join('\n');
}

async function describeExamState(exam: any, categories: string[]): Promise<string> {
  const state = exam?.examState;
  if (!state) return 'Klausur-Status nicht initialisiert.';

  const wanted = new Set(categories.map(category => category.toLowerCase()));
  const unknown = categories.filter(category => !KNOWN_STATE_CATEGORIES.includes(category.toLowerCase()));
  const blocks: string[] = [];

  if (wanted.has('workflow')) blocks.push(describeWorkflow(state));
  if (wanted.has('grading')) blocks.push(describeGrading(state));
  if (wanted.has('uncertainties')) blocks.push(await describeUncertainties(exam, state));

  if (unknown.length) {
    blocks.push(
      `[SYSTEM] Unbekannte Status-Kategorie(n) ignoriert: ${unknown.join(
        ', ',
      )}. Erlaubt sind: workflow, grading, uncertainties.`,
    );
  }

  return blocks.join('\n\n');
}

// ---------------------------------------------------------------------------
// Tool: query_exams
// ---------------------------------------------------------------------------

async function describeExam(
  exam: any,
  taskFields: string[],
  taskPaths: string[],
  stateCategories: string[],
  tier: ExamTier,
): Promise<string> {
  const lines = [
    `Klausur: "${examTitle(exam)}" (ID: ${exam._id})`,
    `- Fach: ${exam.subject ?? 'N/A'} | Thema: ${exam.topic ?? 'N/A'} | Klasse: ${exam.class ?? 'N/A'}`,
  ];

  // METADATA_ONLY (>5 exams): only the base data above — no task content AND no status.
  // STATE_ONLY (4–5 exams): no task content, status still allowed. COMPARE_MODE (2–3 exams): drop the
  // text-heavy masterSolution / gradingScheme, keep description. FULL_DETAILS (1 exam): everything requested.
  const wantDescription = taskFields.includes('description');
  const wantMasterSolution = taskFields.includes('masterSolution') && tier === 'FULL_DETAILS';
  const wantGradingScheme = taskFields.includes('gradingScheme') && tier === 'FULL_DETAILS';
  const renderTasks =
    (tier === 'FULL_DETAILS' || tier === 'COMPARE_MODE') &&
    (wantDescription || wantMasterSolution || wantGradingScheme);

  if (renderTasks) {
    lines.push('', 'Aufgaben:');
    let tasks = (await getMasterSolutionsQuery(String(exam._id))(masterSolutionSelection)) as any[];
    if (taskPaths.length) {
      tasks = tasks.filter(task => matchesAnyPath(String(task.displayTaskNumber || task.taskNumber || ''), taskPaths));
    }
    if (!tasks.length) {
      lines.push(taskPaths.length ? '- (keine passenden Aufgaben)' : '- (keine Aufgaben vorhanden)');
    }
    for (const task of tasks) {
      const path = task.displayTaskNumber || task.taskNumber || '?';
      lines.push(`- Aufgabe ${path}:`);
      if (wantDescription) lines.push(`  Aufgabenstellung: ${String(task.description ?? '').trim() || '(keine)'}`);
      if (wantMasterSolution)
        lines.push(`  Musterlösung: ${String(task.masterSolutionContent ?? '').trim() || '(noch nicht vorhanden)'}`);
      if (wantGradingScheme)
        lines.push(
          `  Bewertungsschema (max. ${task.gradingMaxPoints ?? 0} Punkte): ${
            String(task.gradingCriteria ?? '').trim() || '(noch nicht vorhanden)'
          }`,
        );
    }
  }

  if (stateCategories.length && tier !== 'METADATA_ONLY') {
    lines.push('', `Klausur-Status:\n${await describeExamState(exam, stateCategories)}`);
  }

  return lines.join('\n');
}

interface ExamRequest {
  /** Resolved exam id ('this'/'current' already mapped to the conversation's exam). */
  id: string;
  /** Optional per-exam task-path filter (empty = all tasks of that exam). */
  taskPaths: string[];
}

/**
 * Parses the `exams: [{ examId, taskPaths? }]` selection shared by query_exams and query_submissions.
 * Each exam carries its own optional task filter (exams have different task structures). Resolves
 * 'this'/'current' to the conversation's exam and de-duplicates by resolved id (order preserved).
 */
function parseExamRequests(args: any, ctx: QueryToolContext): { requests: ExamRequest[]; currentMissing: boolean } {
  const rawEntries: any[] = Array.isArray(args.exams) && args.exams.length ? args.exams : [{ examId: 'this' }];

  const byId = new Map<string, string[]>();
  let currentMissing = false;
  for (const entry of rawEntries) {
    const examKey =
      entry && typeof entry === 'object' && typeof entry.examId === 'string' ? entry.examId : String(entry ?? '');
    const paths = entry && typeof entry === 'object' ? toTaskPathArray(entry.taskPaths) : [];
    const id = resolveExamId(examKey, ctx);
    if (id === null) {
      currentMissing = true;
      continue;
    }
    byId.set(id, [...(byId.get(id) ?? []), ...paths]);
  }

  const requests = [...byId.entries()].map(([id, taskPaths]) => ({ id, taskPaths: dedupe(taskPaths) }));
  return { requests, currentMissing };
}

async function runQueryExams(args: any, ctx: QueryToolContext): Promise<QueryToolResult> {
  const userId = await getCurrentUserId();

  const { requests } = parseExamRequests(args, ctx);
  if (!requests.length) return { content: noCurrentExamBlock(await listOwnedExams(userId)) };

  const taskFields = toStringArray(args.taskFields);
  const stateCategories = toStringArray(args.examState);

  // Hard cap: never touch more than MAX_EXAMS_PER_QUERY exams in one call (context + DB protection).
  const cappedRequests = requests.slice(0, MAX_EXAMS_PER_QUERY);
  const droppedExamCount = requests.length - cappedRequests.length;

  // Phase 1: resolve which requested exams actually exist (and belong to the user).
  const found: Array<{ exam: any; taskPaths: string[] }> = [];
  const missing: string[] = [];
  for (const req of cappedRequests) {
    const exam = await getOwnedExam(req.id, userId);
    if (!exam) {
      missing.push(req.id);
      continue;
    }
    found.push({ exam, taskPaths: req.taskPaths });
  }

  // Tier is driven by how many exams we actually render (text volume scales with that).
  const tier = getExamTier(found.length);

  // Phase 2: render each exam under the chosen tier.
  const blocks: string[] = [];
  for (const { exam, taskPaths } of found) {
    blocks.push(await describeExam(exam, taskFields, taskPaths, stateCategories, tier));
  }

  let content = blocks.join('\n\n---\n\n');
  if (missing.length) {
    const block = examNotFoundBlock(missing, await listOwnedExams(userId));
    content = content ? `${content}\n\n${block}` : block;
  }
  if (!content.trim()) content = '[SYSTEM] Keine Klausurdaten verfügbar.';

  const hints: string[] = [];
  if (droppedExamCount > 0) hints.push(examCapHint(droppedExamCount));
  const tierHint = getDiagnosticSystemHint('query_exams', tier, {
    description: taskFields.includes('description'),
    masterSolution: taskFields.includes('masterSolution'),
    gradingScheme: taskFields.includes('gradingScheme'),
    anyTaskField: taskFields.length > 0,
    anyState: stateCategories.length > 0,
  });
  if (tierHint) hints.push(tierHint);
  if (hints.length) content += `\n\n${hints.join('\n')}`;

  return { content: content + ID_HINT };
}

// ---------------------------------------------------------------------------
// Tool: query_submissions
// ---------------------------------------------------------------------------

/** Per-task grading info needed to aggregate points (mirrors the master-solution / summary view). */
interface TaskInfo {
  maxPoints: number;
  needsAnswer: boolean;
}

/** Unwraps the legacy `{ doc: ... }` reactive wrapper some grading subdocuments still carry. */
function unwrapGrading(gradingResult: any): any {
  return gradingResult?.doc ?? gradingResult ?? null;
}

/** Achieved points for one graded node, clamped to [0, maxPoints]. Uses the shared awarded-points helper. */
function achievedPoints(grading: any, maxPoints: number): number {
  return Math.max(0, Math.min(awardedPoints({ gradingResult: grading }), maxPoints));
}

function roundPoints(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Sums achieved and achievable points across a submission's (already path-filtered) task tree.
 * Only `needsAnswer` nodes contribute, which is exactly how the client summary view totals points —
 * so container/parent tasks never double-count their children. Ungraded answerable tasks count toward
 * `max` but contribute 0 to `achieved` (matching the UI).
 */
function sumSubmissionPoints(
  tasks: FlatSubmissionTask[],
  taskInfoByPath: Map<string, TaskInfo>,
): { achieved: number; max: number; gradedCount: number } {
  let achieved = 0;
  let max = 0;
  let gradedCount = 0;
  for (const { pathStr, node } of tasks) {
    const info = taskInfoByPath.get(pathStr);
    if (!info || !info.needsAnswer) continue;
    max += info.maxPoints;
    const grading = unwrapGrading(node?.gradingResult);
    if (grading && (Number.isFinite(grading.actualPointsGiven) || Number.isFinite(grading.score))) {
      achieved += achievedPoints(grading, info.maxPoints);
      gradedCount += 1;
    }
  }
  return { achieved, max, gradedCount };
}

/** "X/Y" total for one student, or "noch nicht bewertet" when nothing in scope has been graded. */
function formatTotalPoints(achieved: number, max: number, gradedCount: number): string {
  if (gradedCount === 0) return 'noch nicht bewertet';
  const a = roundPoints(achieved);
  return max > 0 ? `${a}/${roundPoints(max)}` : `${a}`;
}

/** Bare "X/Y" points for one task (TASK_SCORES tier) — no confidence, approval or explanation. */
function renderBarePoints(gradingResult: any, maxPoints: number | undefined): string {
  const grading = unwrapGrading(gradingResult);
  if (!grading) return '(noch nicht bewertet)';
  const points = Number.isFinite(grading.actualPointsGiven)
    ? grading.actualPointsGiven
    : Number.isFinite(grading.score)
      ? grading.score
      : null;
  if (points == null) return '(noch nicht bewertet)';
  return `${points}${maxPoints != null ? `/${maxPoints}` : ''}`;
}

function renderGradingResult(gradingResult: any, maxPoints: number | undefined): string {
  const grading = unwrapGrading(gradingResult);
  if (!grading) return '(noch nicht bewertet)';

  const points = Number.isFinite(grading.actualPointsGiven)
    ? grading.actualPointsGiven
    : Number.isFinite(grading.score)
      ? grading.score
      : null;
  const parts = [`${points ?? '?'}${maxPoints != null ? `/${maxPoints}` : ''} Punkte`];
  if (grading.confidence) parts.push(`Konfidenz: ${grading.confidence}`);
  if (grading.isApproved === true) parts.push('vom Lehrer bestätigt');
  else if (grading.isApproved === false) parts.push('noch nicht bestätigt');

  let text = parts.join(', ');
  if (grading.explanation) text += `\n    Begründung: ${String(grading.explanation).trim()}`;
  return text;
}

/**
 * Renders one submission under the active tier:
 * - TOTALS_ONLY: a single line `- Schüler: Name | Gesamtpunkte: X/Y` (no task details).
 * - TASK_SCORES: one line per task with bare points; studentAnswer and explanation omitted entirely.
 * - FULL: full output (answers, points, confidence, AI explanations) as requested.
 * (EXAM_AVERAGE never reaches here — it is aggregated per exam, not per student.)
 */
function describeSubmission(
  submission: any,
  taskPaths: string[],
  tier: SubmissionTier,
  includeAnswer: boolean,
  includeGrading: boolean,
  taskInfoByPath: Map<string, TaskInfo>,
): string {
  const name = studentLabel(submission);

  let tasks = flattenSubmissionTasks(submission);
  if (taskPaths.length) tasks = tasks.filter(task => matchesAnyPath(task.pathStr, taskPaths));

  if (tier === 'TOTALS_ONLY') {
    const { achieved, max, gradedCount } = sumSubmissionPoints(tasks, taskInfoByPath);
    return `- Schüler: ${name} | Gesamtpunkte: ${formatTotalPoints(achieved, max, gradedCount)}`;
  }

  const lines = [`Schüler: ${name}`];

  if (tier === 'TASK_SCORES') {
    if (!tasks.length) {
      lines.push('- (keine passenden Aufgaben)');
      return lines.join('\n');
    }
    for (const task of tasks) {
      lines.push(
        `- Aufgabe ${task.pathStr || '?'}: Punkte: ${renderBarePoints(
          task.node?.gradingResult,
          taskInfoByPath.get(task.pathStr)?.maxPoints,
        )}`,
      );
    }
    return lines.join('\n');
  }

  // FULL.
  if (!includeAnswer && !includeGrading) {
    const handled = tasks.map(task => task.pathStr).filter(Boolean);
    lines.push(`- Bearbeitete Aufgaben: ${handled.join(', ') || 'keine'}`);
    return lines.join('\n');
  }

  if (!tasks.length) {
    lines.push('- (keine passenden Aufgaben)');
    return lines.join('\n');
  }

  for (const task of tasks) {
    lines.push(`- Aufgabe ${task.pathStr || '?'}:`);
    if (includeAnswer) lines.push(`  Antwort: ${String(task.node?.studentAnswer ?? '').trim() || '(keine erkannt)'}`);
    if (includeGrading)
      lines.push(
        `  Bewertung: ${renderGradingResult(task.node?.gradingResult, taskInfoByPath.get(task.pathStr)?.maxPoints)}`,
      );
  }
  return lines.join('\n');
}

/** Selects submissions matching any of the requested names (exact, then partial); reports unmatched names. */
function selectSubmissionsByNames(
  allSubmissions: any[],
  studentNames: string[],
): { selected: any[]; missing: string[] } {
  if (!studentNames.length) return { selected: allSubmissions, missing: [] };

  const selected: any[] = [];
  const seen = new Set<string>();
  const missing: string[] = [];

  for (const name of studentNames) {
    const needle = name.toLowerCase();
    let matches = allSubmissions.filter(
      s =>
        String(s?.studentName ?? '')
          .trim()
          .toLowerCase() === needle,
    );
    if (!matches.length)
      matches = allSubmissions.filter(s =>
        String(s?.studentName ?? '')
          .toLowerCase()
          .includes(needle),
      );
    if (!matches.length) {
      missing.push(name);
      continue;
    }
    for (const match of matches) {
      const id = String(match._id);
      if (!seen.has(id)) {
        seen.add(id);
        selected.push(match);
      }
    }
  }
  return { selected, missing };
}

/** Loads a path -> { maxPoints, needsAnswer } map for an exam (used to score / aggregate submissions). */
async function loadTaskInfo(examId: string): Promise<Map<string, TaskInfo>> {
  const map = new Map<string, TaskInfo>();
  const tasks = (await getMasterSolutionsQuery(examId)(masterSolutionSelection)) as any[];
  for (const task of tasks) {
    const path = String(task.displayTaskNumber || task.taskNumber || '');
    if (path) map.set(path, { maxPoints: Number(task.gradingMaxPoints) || 0, needsAnswer: task.needsAnswer !== false });
  }
  return map;
}

/** One exam's submissions matched and counted in Phase 1, ready to render under the global tier. */
interface ExamSubmissionGroup {
  exam: any;
  allSubmissions: any[];
  selected: any[];
  missing: string[];
  taskPaths: string[];
}

/** Renders one exam's submissions under the active tier. EXAM_AVERAGE collapses to a single aggregate line. */
async function renderExamSubmissions(
  group: ExamSubmissionGroup,
  tier: SubmissionTier,
  includeAnswer: boolean,
  includeGrading: boolean,
  studentFiltered: boolean,
): Promise<string> {
  const { exam, allSubmissions, selected, missing, taskPaths } = group;

  // Points are needed for every tier except a FULL query that doesn't ask for grading.
  const needsPoints = tier !== 'FULL' || includeGrading;
  const taskInfoByPath = needsPoints ? await loadTaskInfo(String(exam._id)) : new Map<string, TaskInfo>();

  const lines = [
    `Klausur: "${examTitle(exam)}" (ID: ${exam._id})`,
    `Abgaben: ${selected.length}${studentFiltered ? ' (nach Schülername gefiltert)' : ''}`,
  ];
  if (missing.length) {
    lines.push(
      `Nicht gefundene Schüler: ${missing.join(', ')}. Verfügbare Schüler: ${
        uniqueStudentNames(allSubmissions).join(', ') || 'keine'
      }`,
    );
  }

  if (tier === 'EXAM_AVERAGE') {
    let totalAchieved = 0;
    let totalMax = 0;
    for (const submission of selected) {
      let tasks = flattenSubmissionTasks(submission);
      if (taskPaths.length) tasks = tasks.filter(task => matchesAnyPath(task.pathStr, taskPaths));
      const { achieved, max } = sumSubmissionPoints(tasks, taskInfoByPath);
      totalAchieved += achieved;
      totalMax += max;
    }
    const percent = totalMax > 0 ? Math.round((totalAchieved / totalMax) * 100) : 0;
    lines.push(
      '',
      selected.length
        ? `- Gesamtdurchschnitt dieser Klausur: ${percent}% der erreichbaren Punkte (basiert auf ${selected.length} Abgaben).`
        : '(keine passenden Abgaben)',
    );
    return lines.join('\n');
  }

  const subBlocks = selected.map(submission =>
    describeSubmission(submission, taskPaths, tier, includeAnswer, includeGrading, taskInfoByPath),
  );
  // TOTALS_ONLY is one line per student, so keep those lines tight; other tiers are multi-line blocks.
  const joiner = tier === 'TOTALS_ONLY' ? '\n' : '\n\n';
  lines.push('', subBlocks.length ? subBlocks.join(joiner) : '(keine passenden Abgaben)');
  return lines.join('\n');
}

async function runQuerySubmissions(args: any, ctx: QueryToolContext): Promise<QueryToolResult> {
  const userId = await getCurrentUserId();
  let ownedExamsCache: ExamListEntry[] | null = null;
  const ownedExams = async (): Promise<ExamListEntry[]> => (ownedExamsCache ??= await listOwnedExams(userId));

  // Each exam carries its own optional task-path filter (exams have different task structures).
  const { requests } = parseExamRequests(args, ctx);
  if (!requests.length) return { content: noCurrentExamBlock(await ownedExams()) };

  // Hard cap: never touch more than MAX_EXAMS_PER_QUERY exams in one call (context + DB protection;
  // each exam is one exam lookup plus a full submissions fetch in Phase 1).
  const cappedRequests = requests.slice(0, MAX_EXAMS_PER_QUERY);
  const droppedExamCount = requests.length - cappedRequests.length;

  // Student-name filter (accepts a list; legacy singular `studentName` also accepted). Empty => all students.
  const studentNames = dedupe(
    toStringArray(args.studentNames ?? args.studentName).filter(name => name.toLowerCase() !== 'null'),
  );

  const includeAnswer = args.studentAnswer === true;
  const includeGrading = args.gradingResult === true;
  const requestedTier = parseRequestedSubmissionTier(args.detailLevel);

  // Phase 1: load + match every exam's submissions and count the total BEFORE rendering anything, so the
  // detail tier is chosen from the real volume.
  const found: ExamSubmissionGroup[] = [];
  const notFound: string[] = [];
  for (const req of cappedRequests) {
    const exam = await getOwnedExam(req.id, userId);
    if (!exam) {
      notFound.push(req.id);
      continue;
    }
    const allSubmissions = await getSubmissionsForExam(req.id);
    const { selected, missing } = selectSubmissionsByNames(allSubmissions, studentNames);
    found.push({ exam, allSubmissions, selected, missing, taskPaths: req.taskPaths });
  }

  const requestsCount = found.length;
  const totalSubmissions = found.reduce((sum, group) => sum + group.selected.length, 0);
  const isSingleTaskQuery = requestsCount === 1 && found[0]?.taskPaths.length === 1;
  const autoTier = getSubmissionTier(requestsCount, totalSubmissions, isSingleTaskQuery);
  // The agent may downgrade to a coarser tier (less detail); a finer request is clamped to the auto cap.
  const tier = coarserTier(autoTier, requestedTier);

  // Phase 2: render every exam under the chosen tier (one block per exam, grouped per exam then per student).
  const examBlocks: string[] = [];
  for (const group of found) {
    examBlocks.push(await renderExamSubmissions(group, tier, includeAnswer, includeGrading, studentNames.length > 0));
  }
  for (const id of notFound) {
    examBlocks.push(examNotFoundBlock([id], await ownedExams()));
  }

  let content = examBlocks.join('\n\n---\n\n');
  if (!content.trim()) content = '[SYSTEM] Keine Abgaben verfügbar.';

  const hints: string[] = [];
  if (droppedExamCount > 0) hints.push(examCapHint(droppedExamCount));
  if (requestedTier && SUBMISSION_TIER_RANK[requestedTier] < SUBMISSION_TIER_RANK[autoTier]) {
    hints.push(
      `[SYSTEM] HINWEIS: Der angeforderte Detailgrad '${args.detailLevel}' ist bei dieser Datenmenge nicht verfügbar und wurde aus Performance-Gründen auf einen gröberen Grad begrenzt. Schränke auf weniger Schüler/Klausuren oder eine einzelne Aufgabe ein, um mehr Details zu erhalten.`,
    );
  }
  const tierHint = getDiagnosticSystemHint('query_submissions', tier, {
    studentAnswer: includeAnswer,
    gradingResult: includeGrading,
  });
  if (tierHint) hints.push(tierHint);
  if (hints.length) content += `\n\n${hints.join('\n')}`;

  return { content: content + ID_HINT };
}

// ---------------------------------------------------------------------------
// Tool: get_available_options
// ---------------------------------------------------------------------------

async function runGetAvailableOptions(args: any, ctx: QueryToolContext): Promise<QueryToolResult> {
  const userId = await getCurrentUserId();
  const sections: string[] = [];

  // Fetch the user's exams at most once (used by listings + not-found blocks).
  let ownedExamsCache: ExamListEntry[] | null = null;
  const ownedExams = async (): Promise<ExamListEntry[]> => (ownedExamsCache ??= await listOwnedExams(userId));

  // Fetch each exam's submissions at most once across all sub-options.
  const submissionsCache = new Map<string, any[]>();
  const submissionsFor = async (examId: string): Promise<any[]> => {
    const cached = submissionsCache.get(examId);
    if (cached) return cached;
    const fetched = await getSubmissionsForExam(examId);
    submissionsCache.set(examId, fetched);
    return fetched;
  };

  // Emit the "no current exam" hint at most once, even if several list entries reference 'this'.
  let noCurrentExamShown = false;
  const pushNoCurrentExam = async () => {
    if (noCurrentExamShown) return;
    noCurrentExamShown = true;
    sections.push(noCurrentExamBlock(await ownedExams()));
  };

  if (args.allExamsForCurrentUser === true) {
    sections.push(`Alle Klausuren des aktuellen Nutzers:\n${formatExamList(await ownedExams())}`);
  }

  if (args.allStudentsForCurrentUser === true) {
    const names = new Set<string>();
    for (const exam of await ownedExams()) {
      for (const name of uniqueStudentNames(await submissionsFor(exam.id))) names.add(name);
    }
    sections.push(`Alle Schüler des aktuellen Nutzers:\n${formatNameList([...names])}`);
  }

  // studentsForKnownExams: list of exam ids -> one "students of exam" section per exam.
  for (const rawId of dedupe(toStringArray(args.studentsForKnownExams))) {
    const examId = resolveListedExamId(rawId, ctx);
    if (examId === null) {
      await pushNoCurrentExam();
      continue;
    }
    if (!examId) continue;
    const exam = await getOwnedExam(examId, userId);
    if (!exam) {
      sections.push(examNotFoundBlock([examId], await ownedExams()));
      continue;
    }
    sections.push(
      `Schüler der Klausur "${examTitle(exam)}":\n${formatNameList(uniqueStudentNames(await submissionsFor(examId)))}`,
    );
  }

  // tasksForKnownExams: list of exam ids -> one "task paths of exam" section per exam.
  for (const rawId of dedupe(toStringArray(args.tasksForKnownExams))) {
    const examId = resolveListedExamId(rawId, ctx);
    if (examId === null) {
      await pushNoCurrentExam();
      continue;
    }
    if (!examId) continue;
    const exam = await getOwnedExam(examId, userId);
    if (!exam) {
      sections.push(examNotFoundBlock([examId], await ownedExams()));
      continue;
    }
    const paths = collectExamTaskPaths(exam);
    sections.push(
      `Aufgabenpfade der Klausur "${examTitle(exam)}":\n${
        paths.length ? paths.map(path => `- ${path}`).join('\n') : '- (keine Aufgaben vorhanden)'
      }`,
    );
  }

  // examsForKnownStudents: list of student names -> one "exams of student" section per name.
  for (const studentName of dedupe(toStringArray(args.examsForKnownStudents))) {
    if (studentName.toLowerCase() === 'null') continue;
    const needle = studentName.toLowerCase();
    const matched: ExamListEntry[] = [];
    for (const exam of await ownedExams()) {
      const submissions = await submissionsFor(exam.id);
      if (
        submissions.some(s =>
          String(s?.studentName ?? '')
            .toLowerCase()
            .includes(needle),
        )
      )
        matched.push(exam);
    }
    sections.push(
      `Klausuren mit einer Abgabe von "${studentName}":\n${matched.length ? formatExamList(matched) : '- (keine gefunden)'}`,
    );
  }

  if (!sections.length) {
    return {
      content:
        '[SYSTEM] Es wurde keine Option ausgewählt. Setze mindestens einen der Parameter: allExamsForCurrentUser, allStudentsForCurrentUser, studentsForKnownExams, tasksForKnownExams oder examsForKnownStudents.',
    };
  }

  return { content: sections.join('\n\n---\n\n') + ID_HINT };
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

export async function executeQuery(
  toolName: string,
  args: any,
  ctx: QueryToolContext = { currentExamId: null },
): Promise<QueryToolResult> {
  try {
    switch (toolName) {
      case 'query_exams':
        return await runQueryExams(args ?? {}, ctx);
      case 'query_submissions':
        return await runQuerySubmissions(args ?? {}, ctx);
      case 'get_available_options':
        return await runGetAvailableOptions(args ?? {}, ctx);
      default:
        return { content: `[SYSTEM] Unbekanntes Abfrage-Tool: ${toolName}` };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error({ err: error, toolName, context: 'query-agent' }, 'Fehler bei Tool');
    return { content: `[SYSTEM] Fehler bei der Datenbankabfrage: ${message}` };
  }
}
